import { createTheme, alpha } from '@mui/material/styles';

export const createOceanTheme = (mode = 'light') => {
  // Ocean colors
  const primaryColor = mode === 'light' ? '#0277bd' : '#58a5f0';
  const secondaryColor = mode === 'light' ? '#00838f' : '#4fb3bf';
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryColor,
        light: mode === 'light' ? '#58a5f0' : '#8bd4ff',
        dark: mode === 'light' ? '#004c8c' : '#0069c0',
        contrastText: '#ffffff',
      },
      secondary: {
        main: secondaryColor,
        light: mode === 'light' ? '#4fb3bf' : '#80e5eb',
        dark: mode === 'light' ? '#005662' : '#006064',
        contrastText: '#ffffff',
      },
      background: {
        default: mode === 'light' ? '#f5fafd' : '#051923',
        paper: mode === 'light' ? '#ffffff' : '#0c2e3e',
        subtle: mode === 'light' ? '#e1f5fe' : '#0d3446',
      },
      text: {
        primary: mode === 'light' ? '#0d3446' : '#e1f5fe',
        secondary: mode === 'light' ? '#0277bd' : '#81d4fa',
      },
      error: {
        main: '#d32f2f',
      },
      warning: {
        main: '#f57c00',
      },
      info: {
        main: '#0288d1',
      },
      success: {
        main: '#388e3c',
      },
    },
    typography: {
      fontFamily: '"Nunito", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 500 },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 30, // Pill-shaped buttons
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 20px',
            transition: 'all 0.2s',
          },
          contained: {
            boxShadow: mode === 'light' 
              ? '0 4px 10px rgba(2, 119, 189, 0.2)'
              : '0 4px 10px rgba(0, 0, 0, 0.3)',
            '&:hover': {
              boxShadow: mode === 'light'
                ? '0 6px 15px rgba(2, 119, 189, 0.3)'
                : '0 6px 15px rgba(0, 0, 0, 0.4)',
              transform: 'translateY(-2px)',
            },
          },
          outlined: {
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#ffffff' : '#0c2e3e',
            color: mode === 'light' ? '#0d3446' : '#e1f5fe',
            boxShadow: mode === 'light'
              ? '0 2px 12px rgba(2, 119, 189, 0.1)'
              : '0 2px 12px rgba(0, 0, 0, 0.3)',
            borderBottom: mode === 'light' 
              ? '1px solid #e1f5fe' 
              : '1px solid #051923',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: mode === 'light'
              ? '0 4px 20px rgba(2, 119, 189, 0.08)'
              : '0 4px 20px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-5px) scale(1.01)',
              boxShadow: mode === 'light'
                ? '0 12px 30px rgba(2, 119, 189, 0.15)'
                : '0 12px 30px rgba(0, 0, 0, 0.35)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            fontWeight: 500,
          },
          filled: {
            background: mode === 'light'
              ? `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
              : undefined,
          },
          outlined: {
            borderWidth: 2,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 16,
          },
          elevation1: {
            boxShadow: mode === 'light'
              ? '0 2px 12px rgba(2, 119, 189, 0.06)'
              : '0 2px 12px rgba(0, 0, 0, 0.2)',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#e1f5fe' : '#0d3446',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'light' ? 'rgba(2, 119, 189, 0.04)' : 'rgba(88, 165, 240, 0.08)',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
            color: mode === 'light' ? '#0277bd' : '#e1f5fe',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            height: 6,
          },
        },
      },
    },
  });
};